import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from "classnames";
import capitalize from "lodash/capitalize";
import { memo, useCallback, useState } from "react";
import { usePromise } from "__barrel_optimize__?names=usePromise!=!react-use";
import { useDebounce } from "use-debounce";
import { getTagErrorMessage } from "~/components/Modals/Tags/utils";
import { TagsSelect } from "~/components/Tags/TagsSelect";
import { convertTagToSelectOption } from "~/components/Tags/utils";
import { useCurrentWorkspacePermissionsContext } from "~/providers/CurrentWorkspacePermissionsProvider";
import { useCreateTag } from "~/swr-hooks/tags/useCreateTag";
import { usePrivateTags } from "~/swr-hooks/usePrivateTags";
import { canCreateTag } from "~/utils/permissions/workspacePermissions";
export const PrivateTagsSelect = /*#__PURE__*/ memo((param)=>{
    let { selectedTags = [], onSelectedTagsChanged, canCreateNewTags, className, hideChipsInInput = false, ...props } = param;
    const { createTag } = useCreateTag();
    const [searchValue, setSearchValue] = useState("");
    const [error, setError] = useState("");
    const mounted = usePromise();
    const { data: permissions } = useCurrentWorkspacePermissionsContext();
    const canCreateTags = canCreateTag(permissions);
    const [debouncedSearchValue] = useDebounce(searchValue, 200, {
        trailing: true
    });
    const { data = [], isLoading: isInitialLoading } = usePrivateTags({
        limit: 100,
        isTagsSelect: true,
        search: debouncedSearchValue
    });
    const onCreateTag = useCallback(async (name)=>{
        try {
            setError("");
            onSelectedTagsChanged([
                ...selectedTags,
                {
                    id: "",
                    label: name
                }
            ]);
            const [{ id, label }] = await mounted(createTag(name, "select"));
            onSelectedTagsChanged([
                ...selectedTags,
                {
                    id,
                    label
                }
            ]);
        } catch (error) {
            onSelectedTagsChanged(selectedTags);
            setError(getTagErrorMessage(error, name));
        }
    }, [
        createTag,
        mounted,
        onSelectedTagsChanged,
        selectedTags
    ]);
    const onSelectionChange = useCallback((values)=>{
        setError("");
        const baseTags = values.map((param)=>{
            let { value, label } = param;
            return {
                id: value,
                label
            };
        });
        onSelectedTagsChanged(baseTags);
    }, [
        onSelectedTagsChanged
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: className,
        children: [
            /*#__PURE__*/ _jsx(TagsSelect, {
                tags: data,
                isLoading: isInitialLoading,
                selectedTags: selectedTags.map((tag)=>convertTagToSelectOption(tag)),
                onTagsSelectionChange: onSelectionChange,
                onInputChange: setSearchValue,
                hideChipsInInput: hideChipsInInput,
                onCreateTag: canCreateNewTags && canCreateTags ? onCreateTag : undefined,
                ...props
            }),
            /*#__PURE__*/ _jsx("span", {
                role: "alert",
                className: classNames("absolute text-12 text-red-9", !!error ? "block" : "hidden"),
                "aria-hidden": !error,
                children: capitalize(error)
            })
        ]
    });
});
PrivateTagsSelect.displayName = "PrivateTagsSelect";
